<app-modal #receipt_modal [label]="'Payment Receipt'" [width]="'sm'">
  <div *ngIf="receipt_data" class="bg-gray-50 p-4">

    <!-- Download / Print -->
    <div class="flex justify-end mb-4">
      <button (click)="print_receipt()" class="inline-flex items-center gap-2 px-4 py-2 bg-[#3F41D1] text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
        <i class='bx bx-printer text-[15px]'></i> Download / Print
      </button>
    </div>

    <!-- Receipt Card -->
    <div id="receipt-print-area" class="bg-white p-6 space-y-2 flex flex-col">

      <!-- Section 1: University Header + Receipt Meta -->
      <div class="border border-[#9e9e9e] p-4 flex flex-col items-end gap-4">
        <div *ngIf="receipt_data.academic_year" class="flex items-center gap-2 text-[12px] whitespace-nowrap">
          <span class="font-medium text-[#3c3c3c]">Academic Year :</span>
          <span class="text-[#525252]">{{ receipt_data.academic_year }}</span>
        </div>
        <div class="flex flex-col items-center gap-4 w-full">
          <div class="flex flex-col items-center gap-1">
            <img *ngIf="receipt_data.university?.logo_url" [src]="receipt_data.university.logo_url" class="w-[65px] h-[47px] object-contain" alt="logo">
            <p class="text-[12px] font-medium text-[#3c3c3c] text-center">{{ receipt_data.university?.name || '—' }}</p>
            <p class="text-[10px] text-[#5e5e5e] text-center">{{ receipt_data.university?.address || '' }}</p>
          </div>
          <p class="text-[14px] font-medium text-[#212121]">Digital Copy</p>
          <div class="flex flex-col gap-4 items-start w-full">
            <div class="flex items-center justify-between w-full gap-4">
              <div class="flex gap-2 text-[14px]">
                <span class="font-medium text-[#666] w-[100px] shrink-0">Receipt No</span>
                <span class="text-[#585858]">: {{ receipt_data.receipt_no || '—' }}</span>
              </div>
              <div class="flex gap-2 text-[14px] whitespace-nowrap shrink-0">
                <span class="font-medium text-[#666]">Date</span>
                <span class="text-[#585858]">: {{ get_display_date(receipt_data.receipt_date) }}</span>
              </div>
            </div>
            <div class="flex gap-2 text-[14px] w-full">
              <span class="font-medium text-[#666] w-[100px] shrink-0">Received From</span>
              <span class="text-[#575757]">: {{ receipt_data.student_name || '—' }}</span>
            </div>
            <div class="flex gap-2 text-[14px] w-full">
              <span class="font-medium text-[#666] w-[100px] shrink-0">Roll No.</span>
              <span class="text-[#5b5b5b]">: {{ receipt_data.roll_number || '—' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 2: Program & Exam -->
      <div class="border border-[#9e9e9e] p-4 flex flex-col gap-4">
        <div class="flex gap-2 text-[14px]">
          <span class="font-medium text-[#666] w-[100px] shrink-0">Program</span>
          <span class="text-[#585858]">: {{ receipt_data.program || '—' }}</span>
        </div>
        <div class="flex gap-2 text-[14px]">
          <span class="font-medium text-[#666] w-[100px] shrink-0">Exam</span>
          <span class="text-[#575757]">: {{ receipt_data.exam || '—' }}</span>
        </div>
      </div>

      <!-- Section 3: Items + Total -->
      <div class="border border-[#9e9e9e] pt-2 px-4 pb-4 flex flex-col gap-2 flex-1 min-h-[180px]">
        <div class="flex-1 flex flex-col gap-2">
          <div class="flex items-center justify-between py-2 text-[14px] font-medium">
            <span class="text-[#3d3d3d]">On Account Of</span>
            <span class="text-[#383838]">Amount (₹)</span>
          </div>
          <div class="flex flex-col gap-3 text-[12px]">
            <div *ngFor="let item of receipt_data.items; let i = index" class="flex items-center justify-between gap-4">
              <div class="flex gap-2 items-center">
                <span class="text-[#575757]">{{ i + 1 }}.</span>
                <span class="text-[#535353]">{{ item.description }}</span>
              </div>
              <span class="text-[#585858] shrink-0 tabular-nums">{{ item.amount | number:'1.2-2' }}</span>
            </div>
          </div>
        </div>

        <!-- Total -->
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-[12px] font-medium text-[#313131]">Receipt Total</span>
            <span class="text-[20px] font-medium text-[#383838] tabular-nums">{{ receipt_data.total_amount | number:'1.2-2' }}</span>
          </div>
          <div class="flex gap-0.5 items-center text-[12px]">
            <span class="font-medium text-[#343434] w-[102px] shrink-0">₹ (in words)</span>
            <span class="text-[#595959]">: {{ amount_in_words(receipt_data.total_amount) }} .</span>
          </div>
        </div>
      </div>

      <!-- Section 4: Transaction & Payment -->
      <div class="border border-[#9e9e9e] pt-2 px-4 pb-4 flex flex-col gap-2 text-[12px]">
        <p>
          <span class="font-medium text-[#303030]">Transaction ID&nbsp;&nbsp;&nbsp;: </span>
          <span class="text-[#595959] break-all">{{ receipt_data.gateway_transaction_id || receipt_data.receipt_no }}<ng-container *ngIf="receipt_data.payment_gateway">, {{ receipt_data.payment_gateway }}</ng-container></span>
        </p>
        <p>
          <span class="font-medium text-[#303030]">Payment Method&nbsp;&nbsp;&nbsp;: </span>
          <span class="text-[#595959]">{{ receipt_data.payment_method | uppercase }}</span>
        </p>
      </div>

      <!-- Section 5: Footer – GST + Signature -->
      <div class="flex items-start justify-between px-4 pt-2">
        <div *ngIf="receipt_data.university?.gst_enabled && receipt_data.university?.gstn" class="text-[12px] text-[#555]">
          GST Number: {{ receipt_data.university.gstn }}
        </div>
        <div *ngIf="!receipt_data.university?.gst_enabled || !receipt_data.university?.gstn"></div>
        <div class="flex flex-col items-center gap-2">
          <img *ngIf="receipt_data.university?.signature_url" [src]="receipt_data.university.signature_url" class="h-[48px] w-[50px] object-contain" alt="signature">
          <p class="text-[12px] font-medium text-[#565656] whitespace-nowrap">{{ receipt_data.university?.signatory_name || '' }}</p>
          <p class="text-[12px] italic text-[#a3a3a3] whitespace-nowrap">{{ receipt_data.university?.designation || '' }}</p>
        </div>
      </div>

    </div>
  </div>
</app-modal>
